import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import CurrencyInput from "@/components/ui/currency-input";
import DateField from "@/components/ui/date-field";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SubmitButton } from "@/components/ui/submit-button";
import VehicleSelect from "@/components/ui/vehicle-select";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { Insurance } from "@/interfaces";
import { cn, errorHandler } from "@/lib/utils";
import { insuranceService } from "@/services/api/insurance";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, parseISO } from "date-fns";
import { CalendarIcon, ChevronLeft, Minus, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";

const formSchema = z.object({
  vehicle_id: z.number({
    message: "Veículo é obrigatório",
  }),
  company: z.string().min(1, "Seguradora é obrigatória"),
  premium: z.number().min(0, "Prêmio é obrigatório"),
  policy: z.string().min(1, "Apólice é obrigatória"),
  deductible: z.number().min(0, "Franquia é obrigatória"),
  due_date: z.string().min(1, "Data de vencimento é obrigatória"),
  usage_dates: z.array(z.string()),
});
type FormValues = z.infer<typeof formSchema>;

const InsuranceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [usageDates, setUsageDates] = useState<string[]>([]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      vehicle_id: 0,
      company: "",
      premium: 0,
      policy: "",
      deductible: 0,
      due_date: "",
      usage_dates: [],
    },
  });

  useEffect(() => {
    const loadInsurance = async () => {
      try {
        if (id) {
          const response = await insuranceService.show(Number(id));
          const deductibles =
            response.data.deductibles?.map((d) => d.date) || [];

          const premium = parseFloat(response.data.premium.toString());
          const deductible = parseFloat(response.data.deductible.toString());

          setUsageDates(deductibles);

          form.reset({
            vehicle_id: response.data.vehicle_id,
            company: response.data.company,
            premium: premium,
            policy: response.data.policy,
            deductible: deductible,
            due_date: response.data.due_date,
            usage_dates: deductibles,
          });
        }
      } catch (err) {
        errorHandler(err, "Erro ao carregar seguro");
        navigate("/insurances");
      }
    };

    loadInsurance();
  }, [id, form.reset, navigate]);

  const { handleSubmit, loading } = useFormSubmit({
    id,
    createService: insuranceService.store,
    updateService: insuranceService.update,
    onSuccess: () => navigate("/insurances"),
  });

  const onSubmit = async (values: FormValues) => {
    console.log(values);
    const submissionData: Partial<Insurance> = {
      ...values,
      usage_dates: usageDates.filter((date) => date !== ""),
    };

    await handleSubmit(submissionData);
  };

  const handleAddUsageDate = () => {
    setUsageDates([...usageDates, ""]);
  };

  const handleRemoveUsageDate = (index: number) => {
    const newDates = usageDates.filter((_, i) => i !== index);
    setUsageDates(newDates);
  };

  const handleUsageDateChange = (date: string, index: number) => {
    const newDates = [...usageDates];
    newDates[index] = date;
    setUsageDates(newDates);
  };

  return (
    <Card className="mx-auto">
      <CardHeader>
        <CardTitle>{id ? "Editar Seguro" : "Novo Seguro"}</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <FormField
              control={form.control}
              name="vehicle_id"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Veículo</FormLabel>
                  <VehicleSelect
                    value={field.value}
                    onChange={field.onChange}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="company"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Seguradora</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="premium"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Prêmio</FormLabel>
                  <FormControl>
                    <CurrencyInput
                      value={field.value ?? 0}
                      onChange={(value) => field.onChange(value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="deductible"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Franquia</FormLabel>
                  <FormControl>
                    <CurrencyInput
                      value={field.value ?? 0}
                      onChange={(value) => field.onChange(value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="policy"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Número da apólice</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DateField form={form} label="Data de Vencimento" name="due_date" />

            <FormItem>
              <FormLabel>Datas de utilização da franquia</FormLabel>
              <div className="space-y-2">
                {usageDates.map((date, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-[240px] pl-3 text-left font-normal",
                            !date && "text-muted-foreground",
                          )}
                        >
                          {date ? (
                            date.split("-").reverse().join("/")
                          ) : (
                            <span>Escolha uma data</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={date ? parseISO(date) : undefined}
                          onSelect={(selectedDate) =>
                            selectedDate &&
                            handleUsageDateChange(
                              format(selectedDate, "yyyy-MM-dd"),
                              index,
                            )
                          }
                          autoFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      onClick={() => handleRemoveUsageDate(index)}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddUsageDate}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Data
                </Button>
              </div>
            </FormItem>

            <div className="flex space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/insurances")}
                className="flex items-center space-x-2"
              >
                <ChevronLeft className="w-4 h-4" />
                <span>Voltar</span>
              </Button>

              <SubmitButton loading={loading} />
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default InsuranceForm;
